import React, { useEffect, useState } from 'react';
import { api } from '../../services/authService';
import ColumnTypeahead from './ColumnTypeahead';
import ColumnSearchBar from './ColumnSearchBar';
import FolderPathSelect from './FolderPathSelect';
import CriteriaDateValue from './CriteriaDateValue';
import {
  OP_LABELS,
  RELATIVE_DATE_UNITS,
  conditionFieldErrors,
  emptyCondition,
  opIsInLast,
  opNeedsNoValue,
  operatorsForColumn,
  typeFamily,
} from './criteriaUtils';

const valueListCache = new Map();
const valueListPromises = new Map();

function columnValueKey(condition) {
  const parts = [condition.catalog, condition.schema, condition.table, condition.column].map((v) => String(v || '').trim());
  return parts.every(Boolean) ? parts.join('\u0000') : '';
}

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

function loadColumnValues(condition) {
  const key = columnValueKey(condition);
  if (!key) return Promise.resolve([]);
  if (valueListCache.has(key)) return Promise.resolve(valueListCache.get(key));
  if (!valueListPromises.has(key)) {
    const promise = api
      .get('/api/v2/segments/column-values', {
        params: {
          catalog: condition.catalog,
          schema: condition.schema,
          table: condition.table,
          column_name: condition.column,
        },
      })
      .then((res) => {
        const values = Array.isArray(res.data) ? res.data : [];
        valueListCache.set(key, values);
        return values;
      })
      .catch((e) => {
        valueListPromises.delete(key);
        throw e;
      });
    valueListPromises.set(key, promise);
  }
  return valueListPromises.get(key);
}

const ColumnValueSelect = ({ condition, field, className, onChange }) => {
  const cacheKey = columnValueKey(condition);
  const [values, setValues] = useState(cacheKey && valueListCache.has(cacheKey) ? valueListCache.get(cacheKey) : []);
  const [loading, setLoading] = useState(Boolean(cacheKey) && !valueListCache.has(cacheKey));
  const [loadError, setLoadError] = useState('');
  const currentValue = condition[field] || '';
  const hasCurrentValue = currentValue && !values.includes(currentValue);

  useEffect(() => {
    let cancelled = false;
    setLoadError('');
    if (!cacheKey) {
      setValues([]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(!valueListCache.has(cacheKey));
    loadColumnValues(condition)
      .then((nextValues) => {
        if (!cancelled) setValues(nextValues);
      })
      .catch((e) => {
        if (!cancelled) {
          setValues([]);
          setLoadError(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to load values');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, condition]);

  return (
    <>
      <select
        className={className}
        value={currentValue}
        disabled={loading}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{loading ? 'Loading values…' : 'Select value…'}</option>
        {hasCurrentValue && <option value={currentValue}>{currentValue}</option>}
        {values.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>
      {loadError && (
        <p className="criteria-empty" role="alert">
          {loadError}
        </p>
      )}
    </>
  );
};

function normalizeFolderList(folders) {
  if (!Array.isArray(folders)) return [];
  return folders.map((p) => String(p || '').trim()).filter(Boolean);
}

const CriteriaConditionRow = ({ condition, path, onChange, onRemove }) => {
  const [folderPath, setFolderPath] = useState(() => normalizeFolderList(condition.folder_list));
  const [searchLabel, setSearchLabel] = useState('');
  const fam = typeFamily(condition.column_type);
  const isDateLike = fam === 'date' || fam === 'timestamp';
  const ops = operatorsForColumn(condition.column_type, condition.has_value_list);
  let operator = condition.operator;
  if (!ops.includes(operator)) {
    operator = ops[0];
  }

  const showRelative = opIsInLast(operator);
  const showBetween = operator === 'between';
  const showValue = !opNeedsNoValue(operator) && !showRelative;
  const fieldErrors = conditionFieldErrors({ ...condition, operator });

  const update = (patch) => {
    onChange(path, (node) => ({ ...node, ...patch }));
  };

  const handleFolderPathChange = (nextPath) => {
    const folders = normalizeFolderList(nextPath);
    setFolderPath(folders);
    setSearchLabel('');
    const blank = emptyCondition();
    update({
      catalog: blank.catalog,
      schema: blank.schema,
      table: blank.table,
      column: blank.column,
      column_type: blank.column_type,
      has_value_list: blank.has_value_list,
      operator: blank.operator,
      value: blank.value,
      value2: blank.value2,
      folder_list: folders,
    });
  };

  const applyColumn = (col, folders = folderPath) => {
    const nextFolders = normalizeFolderList(folders);
    setSearchLabel(col.display_name || col.column_name || col.column || '');
    const nextOps = operatorsForColumn(col.column_type, col.has_value_list);
    const nextOp = nextOps.includes(operator) ? operator : nextOps[0];
    update({
      catalog: col.catalog || '',
      schema: col.schema || '',
      table: col.table || '',
      column: col.column || '',
      column_type: col.column_type || '',
      has_value_list: Boolean(col.has_value_list),
      operator: nextOp,
      value: '',
      value2: opIsInLast(nextOp) ? 'days' : '',
      folder_list: nextFolders,
    });
  };

  const handleSearchSelect = (col) => {
    const folders = normalizeFolderList(col.folder_list);
    setFolderPath(folders);
    applyColumn(col, folders);
  };

  const handleSearchClear = () => {
    setSearchLabel('');
    const blank = emptyCondition();
    update({
      catalog: blank.catalog,
      schema: blank.schema,
      table: blank.table,
      column: blank.column,
      column_type: blank.column_type,
      has_value_list: blank.has_value_list,
      operator: blank.operator,
      value: blank.value,
      value2: blank.value2,
      folder_list: folderPath,
    });
  };

  const renderValueInput = (field, className, placeholder) => {
    if (condition.has_value_list) {
      return (
        <ColumnValueSelect
          condition={condition}
          field={field}
          className={className}
          onChange={(next) => update({ [field]: next })}
        />
      );
    }
    if (isDateLike) {
      return (
        <CriteriaDateValue
          family={fam}
          className={className}
          value={condition[field] || ''}
          placeholder={placeholder}
          onChange={(next) => update({ [field]: next })}
        />
      );
    }
    return (
      <input
        type="text"
        className={className}
        value={condition[field] || ''}
        onChange={(e) => update({ [field]: e.target.value })}
      />
    );
  };

  return (
    <div className="criteria-condition">
      <div className="criteria-cond-row criteria-cond-column">
        <ColumnSearchBar
          selectedLabel={searchLabel}
          onSelect={handleSearchSelect}
          onClear={handleSearchClear}
        />
        <FolderPathSelect
          value={folderPath}
          onChange={handleFolderPathChange}
          trailing={
            <ColumnTypeahead
              condition={condition}
              folderPath={folderPath}
              onSelect={applyColumn}
            />
          }
        />
      </div>
      <div className="criteria-cond-row criteria-cond-ops">
        <label className="criteria-inline-label criteria-op-wrap">
          <span>Operator</span>
          <select
            className="criteria-op-field"
            value={operator}
            onChange={(e) => {
              const nextOp = e.target.value;
              const patch = { operator: nextOp };
              if (opIsInLast(nextOp) && !condition.value2) {
                patch.value2 = 'days';
              }
              update(patch);
            }}
          >
            {ops.map((op) => (
              <option key={op} value={op}>
                {OP_LABELS[op] || op}
              </option>
            ))}
          </select>
        </label>

        {showValue && (
          <label className="criteria-inline-label criteria-value-wrap">
            <span>Value</span>
            {renderValueInput('value', 'criteria-value')}
            {fieldErrors.value && (
              <p className="criteria-field-error" role="alert">
                {fieldErrors.value}
              </p>
            )}
          </label>
        )}

        {showBetween && (
          <label className="criteria-inline-label criteria-value2-wrap">
            <span>Second value (between)</span>
            {renderValueInput('value2', 'criteria-value2')}
            {fieldErrors.value2 && (
              <p className="criteria-field-error" role="alert">
                {fieldErrors.value2}
              </p>
            )}
          </label>
        )}

        {showRelative && (
          <>
            <label className="criteria-inline-label criteria-amount-wrap">
              <span>Amount</span>
              <input
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                className="criteria-relative-amount"
                value={condition.value || ''}
                placeholder="e.g. 30"
                onChange={(e) => update({ value: e.target.value })}
              />
              {fieldErrors.value && (
                <p className="criteria-field-error" role="alert">
                  {fieldErrors.value}
                </p>
              )}
            </label>
            <label className="criteria-inline-label criteria-period-wrap">
              <span>Period</span>
              <select
                className="criteria-relative-unit"
                value={(condition.value2 || 'days').toLowerCase()}
                onChange={(e) => update({ value2: e.target.value })}
              >
                {RELATIVE_DATE_UNITS.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        <button type="button" className="criteria-btn-sm criteria-btn-danger criteria-cond-remove" onClick={() => onRemove(path)}>
          Remove
        </button>
      </div>
    </div>
  );
};

export default CriteriaConditionRow;
